import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronRight, FileText } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import ErrorState from '../ui/ErrorState';
import Icon from '../ui/Icon';
import ListRow from '../ui/ListRow';
import PageHeader from '../ui/PageHeader';
import Skeleton from '../ui/Skeleton';
import { clean } from '../shell/copy';
import { fmtTime12 } from './calendar/util';

// /talent (HD-9): the performer's home on the Portal profile (DESIGN rev 2.1 §3.3): their next shoots as List rows, one
// primary action (mark availability), links to their scripts and availability. No tiles, no greeting. Same call:
// GET /api/talent-portal/upcoming-shoots (the API sorts by shoot date).
function shootDate(str) {
  if (!str) return null;
  const [y, m, d] = str.slice(0, 10).split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...(y !== new Date().getFullYear() ? { year: 'numeric' } : {}) });
}

export default function TalentDashboard() {
  const navigate = useNavigate();
  const [shoots, setShoots] = useState([]);
  const [state, setState] = useState('loading');   // loading | data | failed

  const load = useCallback(() => {
    setState('loading');
    api.get('/api/talent-portal/upcoming-shoots')
      .then(r => { setShoots(r.data); setState('data'); })
      .catch(() => setState('failed'));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <main className="ui ui-page ui-page--portal">
      <PageHeader title="Your next shoots"
        actions={<Button variant="primary" portal icon={CalendarDays} onClick={() => navigate('/talent/availability')}>Set when you can shoot</Button>} />

      <section className="ui-section" aria-label="Your next shoots">
        {state === 'loading' && (
          <div className="ui-region ui-list" aria-busy="true">
            {[0, 1, 2].map(i => <ListRow key={i} portal title={<Skeleton width="40%" label={i === 0 ? 'Loading your shoots' : undefined} />} meta={<Skeleton width="20%" />} />)}
          </div>
        )}
        {state === 'failed' && <ErrorState title="Your shoots did not load" message="Check your connection and try again." onRetry={load} />}
        {state === 'data' && shoots.length === 0 && (
          <p className="ui-p ui-text-2">No shoots are scheduled for you yet. When the team schedules one, it shows here with its date and script.</p>
        )}
        {state === 'data' && shoots.length > 0 && (
          <div className="ui-region ui-list">
            {shoots.map(shoot => {
              const scriptId = shoot.script?.id;
              const date = shootDate(shoot.shootScheduledDate);
              return (
                <ListRow key={shoot.id} portal
                  title={clean(shoot.script?.scriptName) || 'Unscripted shoot'}
                  meta={clean(shoot.client?.name)}
                  trailing={(
                    <>
                      <span className="ui-stack ui-stack--tight ui-stack--end">
                        {date && <span>{date}</span>}
                        {shoot.shootTimeSlot && <span className="ui-mono">{fmtTime12(shoot.shootTimeSlot)}</span>}
                      </span>
                      {scriptId && <Icon icon={ChevronRight} size={20} />}
                    </>
                  )}
                  onClick={scriptId ? () => navigate(`/talent/scripts?script=${scriptId}`) : undefined} />
              );
            })}
          </div>
        )}
      </section>

      <section className="ui-section" aria-label="Your pages">
        <div className="ui-region ui-list">
          <ListRow portal leading={<Icon icon={FileText} size={20} />} title="Your scripts" meta="Read the scripts for your shoots"
            trailing={<Icon icon={ChevronRight} size={20} />} onClick={() => navigate('/talent/scripts')} />
          <ListRow portal leading={<Icon icon={CalendarDays} size={20} />} title="When you can shoot" meta="Mark the days you are free"
            trailing={<Icon icon={ChevronRight} size={20} />} onClick={() => navigate('/talent/availability')} />
        </div>
      </section>
    </main>
  );
}
